// filepath: apps/web/src/features/promote/PromotionReviewGatePage.tsx
//
// FR-UI-PROM-2 — non-skippable human-review gate page.
//
// AC-4 NON-SKIPPABLE CONTRACT (enumerated and enforced in code):
//
//   Every plausible skip path was considered and explicitly rejected.
//   The list below documents the contract; the static-guard additions
//   in apps/web/src/runtime/staticGuards.test.ts (PROMOTE_SKIP_PATH_RE)
//   enforce that NO future contributor re-introduces a skip path.
//
//   1. NO URL query-param bypass:
//      - No `?skip=true`, `?auto_approve=true`, `?bypass_review=true`
//        handler is wired. The page reads `?reviewId=<uuid>` from the
//        route — and ONLY `reviewId`. Any other query-param is
//        silently ignored.
//      - The static guard (PROMOTE_SKIP_PATH_RE) catches future
//        URLSearchParams.get('skip' | 'auto_approve' | 'bypass_review')
//        calls in this feature subtree.
//
//   2. NO localStorage / sessionStorage auto-approve flag:
//      - The page never reads localStorage.getItem('promote_*') or
//        sessionStorage.getItem('promote_*'). The static guard
//        matches those patterns.
//
//   3. NO window.confirm() skip path:
//      - The page does not call window.confirm() with any "Skip
//        review" / "Bypass review" string. Static guard catches.
//
//   4. NO hidden-DOM auto-click / MutationObserver tick:
//      - The page does not auto-tick the irreversibility checkbox
//        or auto-click Approve / Reject from JS. Both controls are
//        gated on user input only.
//
//   5. NO __DEV__ / process.env.NODE_ENV skip path:
//      - The page does NOT branch on import.meta.env.DEV /
//        process.env.NODE_ENV to skip the gate. The gate runs in
//        every environment — staging, production, dev.
//
//   6. NO `__SKIP_REVIEW__` / similar global flag:
//      - The page does not read any global flag. The static guard
//        matches `__SKIP_REVIEW__` and `__SKIP_PROMOTION_REVIEW__`
//        string literals.
//
//   7. Mandatory 5 error states (loading / fetch-failure /
//      permission-denied / stale-version / rate-limit):
//      - Each state is handled below. None of them auto-redirects
//      to a "skip review" path or auto-decides the review.
//
//   8. No auto-retry on 409 (INV-7 / FR-UI-CORE-6 pattern):
//      - Approve and Reject mutations both retry: false. A 409
//      from the server surfaces to the reviewer via the existing
//      conflict UX (useRejectPromotion throws; the page shows an
//      alert).
//
//   9. Post-decision state hides both Approve and Reject buttons:
//      - When review.status !== 'pending_review', neither control
//      renders. The reviewer cannot mutate a terminal-state row.
//
//  10. Mandatory content always visible once data loads:
//      - The diff (AC-1), assertions list (AC-2), and NEEDS_REVIEW
//      viewer (AC-3) are all rendered unconditionally — none is
//      collapsible-by-default or hidden behind a tab/toggle that
//      the reviewer could skip past.
//
//  11. Both Approve AND Reject are gated on the irreversibility
//      checkbox (FR-UI-PROM-2 stronger interpretation; FR-UI-PROM-1
//      only gated Approve). When the plan has contract steps,
//      `canApprove` AND `canReject` both require `checkboxChecked`.
//      See canApprove / canReject below.

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { usePromotionReview } from "./usePromotionReview";
import { useApprovePromotion } from "./useApprovePromotion";
import { DiffView } from "./DiffView";
import { IrreversibilityCheckbox } from "./IrreversibilityCheckbox";
import { AssertionsList } from "./AssertionsList";
import { NeedsReviewViewer } from "./NeedsReviewViewer";
import { RejectReasonDialog } from "./RejectReasonDialog";
import type { DefinitionDiff, ReviewContext } from "./types";

interface PromotionReviewGatePageProps {
  reviewId: string;
}

export function PromotionReviewGatePage({
  reviewId,
}: PromotionReviewGatePageProps): React.ReactElement {
  const { data, isLoading, error } = usePromotionReview(reviewId);
  const approveMutation = useApprovePromotion();
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  // FR-UI-PROM-2 5-state rendering — each is exhaustive and never
  // auto-decides the review on the reviewer's behalf.
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  if (error) {
    return <ErrorState error={error} />;
  }
  if (!data) {
    return <p className="text-muted-foreground text-sm">Review not found.</p>;
  }

  return (
    <LoadedReview
      data={data}
      checkboxChecked={checkboxChecked}
      setCheckboxChecked={setCheckboxChecked}
      approveMutation={approveMutation}
      rejectOpen={rejectOpen}
      setRejectOpen={setRejectOpen}
    />
  );
}

interface LoadedReviewProps {
  data: ReviewContext;
  checkboxChecked: boolean;
  setCheckboxChecked: (v: boolean) => void;
  approveMutation: ReturnType<typeof useApprovePromotion>;
  rejectOpen: boolean;
  setRejectOpen: (v: boolean) => void;
}

function LoadedReview({
  data,
  checkboxChecked,
  setCheckboxChecked,
  approveMutation,
  rejectOpen,
  setRejectOpen,
}: LoadedReviewProps): React.ReactElement {
  const { t } = useTranslation();
  const { review, diff_report, artifact } = data;
  const diffs: DefinitionDiff[] = diff_report ? [diff_report] : [];

  const hasIrreversible = diffs.some(
    (d) =>
      d.irreversible &&
      Array.isArray(d.contract_steps) &&
      d.contract_steps.length > 0,
  );
  const requiresCheckbox = hasIrreversible;

  // AC-4 enforcement #11: BOTH controls require the checkbox.
  // The reviewer cannot approve or reject an irreversible plan
  // until they explicitly acknowledge irreversibility.
  const canApprove = !requiresCheckbox || checkboxChecked;
  const canReject = !requiresCheckbox || checkboxChecked;

  const isPending = review.status === "pending_review";

  async function handleApprove() {
    if (!canApprove) return;
    await approveMutation.mutateAsync({
      reviewId: review.id,
      confirmIrreversible: requiresCheckbox,
    });
  }

  return (
    <div className="space-y-6" data-testid="promotion-review-gate-page">
      <ReviewStatusBanner status={review.status} />

      {/* AC-1: complete diff (always visible once data loads) */}
      <section aria-labelledby="gate-section-diff">
        <h2 id="gate-section-diff" className="text-lg font-semibold mb-3">
          {t("promote.gate.section.diff")}
        </h2>
        {diffs.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("promote.gate.diff.empty")}
          </p>
        ) : (
          <DiffView diffs={diffs} />
        )}
      </section>

      {/* AC-2: carried assertions list (always visible) */}
      <section aria-labelledby="gate-section-assertions">
        <h2 id="gate-section-assertions" className="text-lg font-semibold mb-3">
          {t("promote.gate.section.assertions")}
        </h2>
        <AssertionsList artifact={artifact} />
      </section>

      {/* AC-3: NEEDS_REVIEW package viewer (always visible) */}
      <section aria-labelledby="gate-section-artifact">
        <h2 id="gate-section-artifact" className="text-lg font-semibold mb-3">
          {t("promote.gate.section.artifact")}
        </h2>
        <NeedsReviewViewer artifact={artifact} />
      </section>

      {/* Irreversibility acknowledgement — only when plan has contract steps */}
      {requiresCheckbox && (
        <section aria-labelledby="gate-section-acknowledgement">
          <h2
            id="gate-section-acknowledgement"
            className="text-lg font-semibold mb-3 sr-only"
          >
            {t("promote.gate.required_acknowledgement")}
          </h2>
          <IrreversibilityCheckbox
            checked={checkboxChecked}
            onChange={setCheckboxChecked}
          />
        </section>
      )}

      {/* AC-4 decision controls — Approve + Reject */}
      {isPending && (
        <section
          aria-labelledby="gate-section-decision"
          className="flex items-center gap-3"
        >
          <h2 id="gate-section-decision" className="sr-only">
            Decision
          </h2>
          <button
            type="button"
            onClick={handleApprove}
            disabled={!canApprove || approveMutation.isPending}
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="approve-button"
            aria-disabled={!canApprove || approveMutation.isPending}
          >
            {approveMutation.isPending
              ? t("promote.gate.buttons.approving")
              : t("promote.gate.buttons.approve")}
          </button>
          <button
            type="button"
            onClick={() => setRejectOpen(true)}
            disabled={!canReject || approveMutation.isPending}
            className="rounded border border-destructive px-4 py-2 text-sm font-medium text-destructive disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="reject-button"
            aria-disabled={!canReject || approveMutation.isPending}
          >
            {t("promote.gate.buttons.reject")}
          </button>
          {approveMutation.isError && (
            <p
              className="text-sm text-destructive"
              role="alert"
              data-testid="approve-error"
            >
              {t("promote.gate.approve.error")}
            </p>
          )}
        </section>
      )}

      <RejectReasonDialog
        reviewId={review.id}
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onRejected={() => setRejectOpen(false)}
      />
    </div>
  );
}

function ReviewStatusBanner({
  status,
}: {
  status: string;
}): React.ReactElement {
  const colors: Record<string, string> = {
    pending_review: "bg-yellow-50 border-yellow-200 text-yellow-800",
    approved: "bg-green-50 border-green-200 text-green-800",
    rejected: "bg-red-50 border-red-200 text-red-800",
    applied: "bg-blue-50 border-blue-200 text-blue-800",
    failed: "bg-red-50 border-red-200 text-red-800",
    superseded: "bg-gray-50 border-gray-200 text-gray-800",
  };
  const cls = colors[status] ?? "bg-gray-50 border-gray-200 text-gray-800";
  return (
    <div className={`rounded border px-3 py-2 text-sm ${cls}`} role="status">
      Status:{" "}
      <span className="font-medium capitalize">{status.replace("_", " ")}</span>
    </div>
  );
}

function LoadingSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4 animate-pulse" data-testid="loading-skeleton">
      <div className="h-12 bg-muted rounded" />
      <div className="h-32 bg-muted rounded" />
      <div className="h-8 bg-muted rounded" />
    </div>
  );
}

function ErrorState({ error }: { error: Error }): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div
      className="text-destructive text-sm"
      role="alert"
      data-testid="error-state"
    >
      {t("promote.gate.error.load_failed")} {error.message}
    </div>
  );
}
